import { z } from 'zod';

export const UUIDSchema = z.uuid();

export const Time = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
  WEEK: 7 * 24 * 60 * 60 * 1000,
  MONTH_30: 30 * 24 * 60 * 60 * 1000,
};

/** True when `queryKey` starts with every part of `prefix`, compared by identity. */
export const hasKeyPrefix = (
  queryKey: readonly unknown[],
  prefix: readonly unknown[],
): boolean => prefix.every((part, index) => queryKey[index] === part);

export type ScopedQuery<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: Error; retry: () => void }
  | { status: 'empty' }
  | { status: 'ready'; data: T };

type QueryLike<T> = {
  data?: T;
  isLoading: boolean;
  error: unknown;
  refetch: () => unknown;
};

const asError = (value: unknown): Error =>
  value instanceof Error ? value : new Error(String(value));

export const toScopedQuery = <T>(
  query: QueryLike<T>,
  options: { enabled: boolean; isEmpty: (data: T) => boolean },
): ScopedQuery<T> => {
  if (!options.enabled) {
    return { status: 'idle' };
  }
  if (query.error) {
    return {
      status: 'error',
      error: asError(query.error),
      retry: () => {
        query.refetch();
      },
    };
  }
  if (query.isLoading || query.data === undefined) {
    return { status: 'loading' };
  }
  if (options.isEmpty(query.data)) {
    return { status: 'empty' };
  }
  return { status: 'ready', data: query.data };
};
